import { Box, Button, Card, Stack, Typography } from '@mui/material'
import React from 'react'
import { QueryStatus } from 'react-query'
import shallow from 'zustand/shallow'

import PageSection from 'components/common/PageSection'
import { ServiceProcessSelection } from 'components/services/create/ServiceProcessSelection'
import { useProcessViewStore } from 'hooks/use-process-view'
import theme from 'theme'
import { unique } from 'utils/list'

export const ServiceProcessDependenciesSelection: React.FC<{
  handleGetProcessesView: () => void
  processViewError: string
  refetchStatus: QueryStatus
  onRetry: () => void
}> = ({ handleGetProcessesView, processViewError, refetchStatus, onRetry }) => {
  const {
    hasNextPage,
    processes: proccessesData,
    status
  } = useProcessViewStore(
    (state) => ({
      hasNextPage: state.hasNextPage,
      processes: state.processes,
      status: state.status
    }),
    shallow
  )

  const processes = unique(Object.entries(proccessesData).map(([, v]) => v))

  const isLoading = refetchStatus === 'loading' || status === 'loading'
  const hasError = refetchStatus === 'error' || status === 'error'

  return processViewError ? (
    <Card
      data-test-service-process-dependencies-selection-process-view-error
      css={{ padding: theme.space[3], width: 'max-content' }}
    >
      <Typography
        data-test-service-process-dependencies-selection-process-view-error-text
        color="error"
      >
        Error retrieving processes: {processViewError}
      </Typography>
      <Button
        data-test-service-process-dependencies-selection-process-view-error-button
        css={{ marginTop: theme.space[2] }}
        variant="outlined"
        onClick={handleGetProcessesView}
      >
        Retry
      </Button>
    </Card>
  ) : (
    <PageSection
      hasError={hasError}
      isLoading={isLoading}
      renderError={() => (
        <>
          <Typography
            data-test-service-process-dependencies-selection-error-text
          >
            There was an issue getting a list of your processes. Please click
            back and try again later.
          </Typography>
          <Button
            data-test-service-process-dependencies-selection-error-button
            onClick={onRetry}
          >
            Retry
          </Button>
        </>
      )}
    >
      <Stack gap={3}>
        <Box>
          <Typography
            data-test-service-process-dependencies-selection-description
            css={{ marginBottom: theme.space[2] }}
            variant="body1"
          >
            Gremlin will use this process to discover dependencies by examining
            network traffic.
          </Typography>
        </Box>
        <ServiceProcessSelection
          hasNextPage={hasNextPage}
          processes={processes}
        />
        <Box />
      </Stack>
    </PageSection>
  )
}
